
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import { Heart, Filter, Grid, List, X, ChevronDown, SlidersHorizontal, Check, Eye } from "lucide-react";
import { useWishlistStore, useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { productsApi } from "@/lib/api";
import { getSmartProductPlaceholder, inputPlaceholders, emptyStates } from "@/lib/placeholders";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A to Z" },
  { value: "name-desc", label: "Name: Z to A" },
];

export default function ProductsPage() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedClassifications, setSelectedClassifications] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [inStockOnly, setInStockOnly] = useState(false);
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  
  // Get collection from URL params
  const [collectionSlug, setCollectionSlug] = useState<string | null>(null);
  
  useEffect(() => {
    // Get collection from URL search params on mount
    const params = new URLSearchParams(window.location.search);
    const collection = params.get("collection");
    setCollectionSlug(collection);
  }, []);

  const { addItem: addToWishlist, isInWishlist, removeItem: removeFromWishlist, addItemToBackend, removeItemFromBackend } = useWishlistStore();
  const { isAuthenticated, token } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);

  const categories = ["Necklaces", "Earrings", "Bracelets", "Rings", "Sets", "Accessories"];
  const classifications = ["Bridal", "Workwear", "Mehndi", "Heritage", "Dailywear"];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch products function
  const fetchProducts = async (pageNum: number, append: boolean = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setPage(1);
      }
      
      const params: any = {
        page: pageNum,
        limit: 50,
      };
      
      // Add collection filter from URL params
      if (collectionSlug) {
        params.collection = collectionSlug;
      }
      
      if (selectedCategories.length > 0) {
        params.category = selectedCategories.join(",");
      }
      if (selectedClassifications.length > 0) {
        params.classification = selectedClassifications.join(",");
      }
      if (priceRange.min > 0 || priceRange.max < 100000) {
        params.minPrice = priceRange.min;
        params.maxPrice = priceRange.max;
      }
      if (inStockOnly) {
        params.inStock = true;
      }
      if (onSaleOnly) {
        params.onSale = true;
      }

      const data = await productsApi.getAll(params);
      const newProducts = data.products || data || [];
      
      if (append) {
        setProducts((prev) => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }
      
      // Check if there are more products
      setHasMore(newProducts.length === 50);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchProducts(1, false);
  }, [collectionSlug, selectedCategories, selectedClassifications, priceRange, inStockOnly, onSaleOnly]);

  // Load more products
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, true);
  };

  const handleWishlistToggle = async (product: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is authenticated
    if (!isAuthenticated || !token) {
      setPendingProductId(product.id);
      setShowLoginDialog(true);
      return;
    }
    
    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist
        const success = await removeItemFromBackend(product.id, token);
        if (success) {
          removeFromWishlist(product.id);
          toast.success("Removed from wishlist");
        } else {
          toast.error("Failed to remove from wishlist");
        }
      } else {
        // Add to wishlist
        const success = await addItemToBackend(product.id, token);
        if (success) {
          addToWishlist({
            id: product.id,
            name: product.name,
            price: product.price || 0,
            originalPrice: product.original_price || undefined,
            image: product.image_url || "/product1.png",
            slug: product.slug,
          });
          toast.success("Added to wishlist!");
        } else {
          toast.error("Failed to add to wishlist");
        }
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleClassificationToggle = (classification: string) => {
    setSelectedClassifications((prev) =>
      prev.includes(classification)
        ? prev.filter((c) => c !== classification)
        : [...prev, classification]
    );
  };

  const removeCategoryFilter = (category: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  const removeClassificationFilter = (classification: string) => {
    setSelectedClassifications((prev) => prev.filter((c) => c !== classification));
  };

  const removePriceFilter = () => {
    setPriceRange({ min: 0, max: 100000 });
  };

  const removeInStockFilter = () => {
    setInStockOnly(false);
  };

  const removeOnSaleFilter = () => {
    setOnSaleOnly(false);
  };

  const clearFilters = () => {
    setCollectionSlug(null);
    setSelectedCategories([]);
    setSelectedClassifications([]);
    setPriceRange({ min: 0, max: 100000 });
    setInStockOnly(false);
    setOnSaleOnly(false);
    // Update URL to remove all filters
    const url = new URL(window.location.href);
    url.searchParams.delete("collection");
    window.history.pushState({}, "", url.toString());
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    const priceA = a.price || 0;
    const priceB = b.price || 0;
    switch (sortBy) {
      case "price-low":
        return priceA - priceB;
      case "price-high":
        return priceB - priceA;
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "newest":
      default:
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    }
  });

  const activeFiltersCount =
    (collectionSlug ? 1 : 0) +
    selectedCategories.length +
    selectedClassifications.length +
    (priceRange.min > 0 || priceRange.max < 100000 ? 1 : 0) +
    (inStockOnly ? 1 : 0) +
    (onSaleOnly ? 1 : 0);

  // Remove collection filter
  const removeCollectionFilter = () => {
    setCollectionSlug(null);
    // Update URL without collection parameter
    const url = new URL(window.location.href);
    url.searchParams.delete("collection");
    window.history.pushState({}, "", url.toString());
  };

  // Get active filter chips
  const getActiveFilterChips = () => {
    const chips: Array<{ id: string; label: string; onRemove: () => void }> = [];
    
    // Add collection filter chip if present
    if (collectionSlug) {
      chips.push({
        id: "collection",
        label: `Collection: ${collectionSlug.charAt(0).toUpperCase() + collectionSlug.slice(1)}`,
        onRemove: removeCollectionFilter,
      });
    }
    
    selectedCategories.forEach((cat) => {
      chips.push({
        id: `category-${cat}`,
        label: cat,
        onRemove: () => removeCategoryFilter(cat),
      });
    });
    
    selectedClassifications.forEach((cls) => {
      chips.push({
        id: `classification-${cls}`,
        label: cls,
        onRemove: () => removeClassificationFilter(cls),
      });
    });
    
    if (priceRange.min > 0 || priceRange.max < 100000) {
      chips.push({
        id: "price-range",
        label: `₹${priceRange.min.toLocaleString()} - ₹${priceRange.max === 100000 ? "∞" : priceRange.max.toLocaleString()}`,
        onRemove: removePriceFilter,
      });
    }
    
    if (inStockOnly) {
      chips.push({
        id: "in-stock",
        label: "In Stock Only",
        onRemove: removeInStockFilter,
      });
    }
    
    if (onSaleOnly) {
      chips.push({
        id: "on-sale",
        label: "On Sale",
        onRemove: removeOnSaleFilter,
      });
    }
    
    return chips;
  };

  const activeFilterChips = getActiveFilterChips();
  const selectedSortLabel = sortOptions.find((opt) => opt.value === sortBy)?.label || "Newest First";

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow pt-24 pb-24">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-playfair mb-4">
              {collectionSlug ? (
                <>
                  <span className="gradient-text">{collectionSlug.charAt(0).toUpperCase() + collectionSlug.slice(1)}</span> Collection
                </>
              ) : (
                <>
                  All <span className="gradient-text">Products</span>
                </>
              )}
            </h1>
            <p className="text-gray-600">
              {collectionSlug 
                ? `Discover our exquisite ${collectionSlug} jewellery collection`
                : "Discover our complete collection of exquisite jewellery"
              }
            </p>
          </motion.div>

          {/* Filters and Controls Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 sticky top-24 z-30">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
                
                {/* Custom Sort Dropdown */}
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors min-w-[180px] justify-between"
                  >
                    <span className="text-sm font-medium">{selectedSortLabel}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSortDropdown ? "rotate-180" : ""}`} />
                  </button>
                  
                  <AnimatePresence>
                    {showSortDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                      >
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                              sortBy === option.value ? "bg-purple-50" : ""
                            }`}
                          >
                            <span className="text-sm">{option.label}</span>
                            {sortBy === option.value && (
                              <Check className="w-4 h-4 text-purple-600" />
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear All</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {sortedProducts.length} products
                </span>
              </div>
            </div>

            {/* Active Filter Chips */}
            {activeFilterChips.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {activeFilterChips.map((chip) => (
                    <motion.div
                      key={chip.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      <span>{chip.label}</span>
                      <button
                        onClick={chip.onRemove}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${chip.label} filter`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Filter Drawer */}
          <AnimatePresence>
            {showFilters && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowFilters(false)}
                  className="fixed inset-0 bg-black/50 z-40"
                />
                
                {/* Drawer */}
                <motion.div
                  initial={{ x: -400 }}
                  animate={{ x: 0 }}
                  exit={{ x: -400 }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-2xl z-50 overflow-y-auto"
                >
                  <div className="p-6">
                    {/* Drawer Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold font-playfair">Filters</h2>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Categories */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Categories</h3>
                      <div className="space-y-2">
                        {categories.map((category) => (
                          <label
                            key={category}
                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(category)}
                              onChange={() => handleCategoryToggle(category)}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm text-gray-700">{category}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Classifications */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Collections</h3>
                      <div className="space-y-2">
                        {classifications.map((classification) => (
                          <label
                            key={classification}
                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedClassifications.includes(classification)}
                              onChange={() => handleClassificationToggle(classification)}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm text-gray-700">{classification}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder={inputPlaceholders.minPrice}
                            value={priceRange.min || ""}
                            onChange={(e) =>
                              setPriceRange({
                                ...priceRange,
                                min: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                          <span className="text-gray-500">-</span>
                          <input
                            type="number"
                            placeholder={inputPlaceholders.maxPrice}
                            value={priceRange.max === 100000 ? "" : priceRange.max}
                            onChange={(e) =>
                              setPriceRange({
                                ...priceRange,
                                max: parseInt(e.target.value) || 100000,
                              })
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Other Filters */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Other</h3>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={inStockOnly}
                            onChange={(e) => setInStockOnly(e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">In Stock Only</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={onSaleOnly}
                            onChange={(e) => setOnSaleOnly(e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">On Sale Only</span>
                        </label>
                      </div>
                    </div>

                    {/* Apply Button */}
                    <button
                      onClick={() => setShowFilters(false)}
                      className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold"
                    >
                      Apply Filters
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="group relative">
                  <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-1/2 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-2">{emptyStates.products.title}</p>
              <p className="text-gray-500 mb-6">{emptyStates.products.description}</p>
              <button
                onClick={clearFilters}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
                {sortedProducts.map((product: any, index) => {
                  const discount = calculateDiscount(product.price, product.original_price);
                  // Get primary image from images array, or fallback to image_url
                  const primaryImage = product.images?.find((img: any) => img.is_primary);
                  const imageUrl = primaryImage?.image_url || product.image_url || getSmartProductPlaceholder(product.name);
                  
                  return (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className="group relative"
                    >
                      <Link to={`/products/${product.slug}`}>
                        <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                          {/* Product Image */}
                          <img
                            src={imageUrl}
                            alt={product.name}
                            
                            className="object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getSmartProductPlaceholder(product.name);
                            }}
                          />

                          {/* Stock Indicator */}
                          {product.stock <= 5 && (
                            <div className="absolute top-4 right-4 z-10">
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500 text-white">
                                Only {product.stock} left
                              </span>
                            </div>
                          )}

                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex space-x-4">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                                aria-label="Quick view"
                              >
                                <Eye className="w-5 h-5 text-gray-900" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleWishlistToggle(product, e)}
                                className={`p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors ${
                                  isInWishlist(product.id) ? "text-red-500" : "text-gray-900"
                                }`}
                                aria-label="Add to wishlist"
                              >
                                <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? "-current" : ""}`} />
                              </motion.button>
                            </div>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="mt-4 space-y-2">
                          <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                            {product.name}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="text-xl font-bold text-gray-900">
                              ₹{product.price.toLocaleString()}
                            </span>
                            {product.original_price && discount > 0 && (
                              <>
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{product.original_price.toLocaleString()}
                                </span>
                                <span className="text-sm font-semibold text-green-600">
                                  Save {discount}%
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* View More Button */}
              {hasMore && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center mt-8"
                >
                  <motion.button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    whileHover={{ scale: loadingMore ? 1 : 1.05 }}
                    whileTap={{ scale: loadingMore ? 1 : 0.95 }}
                    className="px-10 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? "Loading..." : "View More"}
                  </motion.button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
      
      {/* OTP Login Dialog */}
      <OTPLoginDialog
        isOpen={showLoginDialog}
        onClose={() => {
          setShowLoginDialog(false);
          setPendingProductId(null);
        }}
        onSuccess={async () => {
          // After successful login, add to wishlist if there's a pending product
          if (pendingProductId) {
            const authState = useAuthStore.getState();
            const product = products.find((p: any) => p.id === pendingProductId);
            if (authState.token && product) {
              const success = await addItemToBackend(pendingProductId, authState.token);
              if (success) {
                addToWishlist({
                  id: product.id,
                  name: product.name,
                  price: product.price || 0,
                  originalPrice: product.original_price || undefined,
                  image: product.image_url || "/product1.png",
                  slug: product.slug,
                });
                toast.success("Added to wishlist!");
              }
            }
            setPendingProductId(null);
          }
        }}
      />
    </div>
  );
}
